import { useState } from 'react'
import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { NiveauAcces } from '../../lib/inclusion/demandes'
import { COULEUR_DU_NIVEAU, DESCRIPTION_DES_NIVEAUX, NIVEAUX, libelleDuNiveau, phraseDuNiveau } from '../../lib/inclusion/niveaux'

export interface NiveauDAccesLu {
  niveau_acces?: NiveauAcces | null
  niveau_acces_le?: string | null
  niveau_acces_motif?: string | null
}

interface NiveauDAccesProps {
  kidId: string
  prenom: string
  decision: NiveauDAccesLu
  onDecide: (decision: NiveauDAccesLu) => void
}

/**
 * Le niveau d'accès d'un enfant en inclusion, depuis sa fiche : l'administration
 * le pose en examinant sa demande, la coordination le change si sa situation
 * évolue (VALIDÉ ÉCLOSION, 26/10/2026). Il s'enregistre seul, hors du reste de
 * la fiche.
 *
 * C'est la base qui garde la règle (`kid_inclusion_garder_le_niveau_acces`) :
 * un autre rôle est refusé, et l'auteur et la date sont les siens. Les besoins
 * déclarés ne bougent pas : l'enfant reste en inclusion (accueil, rapport ONE).
 */
export function NiveauDAcces({ kidId, prenom, decision, onDecide }: NiveauDAccesProps) {
  const actuel = decision.niveau_acces ?? null
  const [choix, setChoix] = useState<NiveauAcces | null>(actuel)
  const [motif, setMotif] = useState(decision.niveau_acces_motif ?? '')
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const inchange = choix === actuel && motif.trim() === (decision.niveau_acces_motif ?? '')

  const enregistrer = async () => {
    if (choix === null) return
    setEnCours(true)
    setErreur(null)
    // Les types générés de Supabase ne connaissent pas encore ces colonnes.
    const db = supabase as unknown as SupabaseClient
    const { data, error } = await db
      .from('kid_inclusion')
      .update({ niveau_acces: choix, niveau_acces_motif: motif.trim() || null })
      .eq('kid_id', kidId)
      .select('niveau_acces, niveau_acces_le, niveau_acces_motif')
      .single()
    setEnCours(false)
    if (error) {
      setErreur(error.message.includes('[niveau-acces-reserve]')
        ? 'Seules la coordination et l\'administration décident du niveau d\'accès.'
        : error.message)
      return
    }
    onDecide(data as NiveauDAccesLu)
  }

  return (
    <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <strong className="text-blue-900">Niveau d'accès</strong>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${COULEUR_DU_NIVEAU[actuel ?? 'aucun']}`}>
          {libelleDuNiveau(actuel)}
        </span>
      </div>
      <p className="text-xs text-blue-900">
        {actuel
          ? phraseDuNiveau(actuel, prenom)
          : `Pas encore examiné : chaque inscription de ${prenom} passe par une demande d'inclusion.`}
        {' '}{prenom} reste en inclusion pour l'accueil et le rapport ONE.
      </p>
      {decision.niveau_acces_le && (
        <p className="text-xs text-gray-600">
          Dernière décision le {new Date(decision.niveau_acces_le).toLocaleDateString('fr-FR')}
          {decision.niveau_acces_motif ? ` — ${decision.niveau_acces_motif}` : ''}
        </p>
      )}
      <fieldset className="space-y-1">
        <legend className="sr-only">Choisir le niveau d'accès de {prenom}</legend>
        {NIVEAUX.map(n => (
          <label key={n} className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="radio"
              name={`niveau-${kidId}`}
              className="mt-1"
              checked={choix === n}
              onChange={() => setChoix(n)}
              disabled={enCours}
            />
            <span>
              <span className="font-medium">Niveau {n} — {DESCRIPTION_DES_NIVEAUX[n].titre}</span>
              <span className="block text-xs text-gray-600">{DESCRIPTION_DES_NIVEAUX[n].description}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <textarea
        value={motif}
        onChange={e => setMotif(e.target.value)}
        rows={2}
        placeholder="Motif (facultatif)"
        aria-label="Motif"
        className="w-full rounded border border-blue-200 bg-white px-2 py-1 text-sm"
      />
      <button
        type="button"
        onClick={() => void enregistrer()}
        disabled={enCours || choix === null || inchange}
        className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {enCours ? 'Enregistrement…' : 'Enregistrer le niveau'}
      </button>
      {erreur && <p className="text-xs text-red-700">{erreur}</p>}
    </div>
  )
}
